import { Attachment, AttachmentScanResult } from '@amzn/awscat-pattern-library-service-model';

import { Clock, systemClock } from '../common/Time';
import { IMplServiceClient } from '../api/IMplServiceClient';
import { log } from '../loggers/Logger';
import { AttachmentTransferError, ServiceCallError } from './Errors';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

const DEFAULT_SCAN_POLL_INTERVAL_MS = 5000;
const DEFAULT_SCAN_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Builds a `.catch` handler that rethrows a `ServiceCallError` as an
 * `AttachmentTransferError` and rethrows anything else.
 */
function handleTransferError(operation: 'upload' | 'load'): (err: unknown) => never {
  return (err: unknown) => {
    if (!(err instanceof ServiceCallError)) throw err;
    throw AttachmentTransferError.fromServiceCall(operation, err);
  };
}

/**
 * Point-in-time view of an attachment, observed by polling
 * `getAttachmentInfo`.
 *
 * `unknown` is the initial snapshot — emitted before the first poll
 * lands. Once the server returns a record we transition to `scanning`
 * (with the record) or, if the scan already finished, `complete`.
 */
export type AttachmentSnapshot =
  | { readonly kind: 'unknown'; readonly attachmentId: string }
  | { readonly kind: 'scanning'; readonly attachmentId: string; readonly attachment: Attachment }
  | { readonly kind: 'complete'; readonly attachment: Attachment }
  | { readonly kind: 'failed'; readonly error: Error };

/**
 * Live monitor for one attachment. Returned by
 * {@link AttachmentManagementService.monitorAttachment}. Subscribe to
 * `changed` to react to snapshot transitions. The poll loop runs until
 * the scan terminates, the timeout elapses, or
 * {@link AttachmentManagementService.stopMonitoring} is called.
 */
export interface AttachmentMonitor {
  readonly snapshot: AttachmentSnapshot;
  on(event: 'changed', listener: () => void): Unsubscribe;
}

/**
 * The scanner's terminal verdict on an attachment, or `pending` while
 * the scan has not yet produced one. Callers gate downloads on this
 * instead of comparing `AttachmentScanResult` values themselves.
 */
export type AttachmentScanVerdict = 'ok' | 'infected' | 'pending';

/** Reduce an attachment's raw `scanResult` to a scan verdict. */
export function classifyScanVerdict(attachment: Attachment): AttachmentScanVerdict {
  if (attachment.scanResult === AttachmentScanResult.OK) return 'ok';
  if (attachment.scanResult === AttachmentScanResult.INFECTED) return 'infected';
  return 'pending';
}

function isTerminalSnapshot(snapshot: AttachmentSnapshot): boolean {
  return snapshot.kind === 'complete' || snapshot.kind === 'failed';
}

function applyPollResult(
  monitor: AttachmentMonitorImpl,
  attachmentId: string,
  attachment: Attachment,
): void {
  if (classifyScanVerdict(attachment) !== 'pending') {
    monitor.setComplete(attachment);
    return;
  }
  monitor.setScanning(attachmentId, attachment);
}

class AttachmentMonitorImpl implements AttachmentMonitor {
  private readonly _emitter = new TypedEmitter<{ changed: () => void }>();
  private _snapshot: AttachmentSnapshot;
  private _isStopped = false;

  constructor(attachmentId: string) {
    this._snapshot = { kind: 'unknown', attachmentId };
  }

  get snapshot(): AttachmentSnapshot {
    return this._snapshot;
  }

  get isStopped(): boolean {
    return this._isStopped;
  }

  on(event: 'changed', listener: () => void): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  stop(): void {
    this._isStopped = true;
    this._emitter.removeAllListeners();
  }

  setScanning(attachmentId: string, attachment: Attachment): void {
    this._snapshot = { kind: 'scanning', attachmentId, attachment };
    this._emitter.emit('changed');
  }

  setComplete(attachment: Attachment): void {
    this._snapshot = { kind: 'complete', attachment };
    this._emitter.emit('changed');
  }

  setFailed(error: Error): void {
    this._snapshot = { kind: 'failed', error };
    this._emitter.emit('changed');
  }
}

/** Tunables for {@link AttachmentManagementService}. Tests pass tighter values. */
export interface AttachmentManagementServiceOptions {
  /** Delay between consecutive `getAttachmentInfo` polls. Defaults to 5s. */
  readonly scanPollIntervalMs?: number;
  /** Total time to wait for a scan result before failing. Defaults to 15 minutes. */
  readonly scanTimeoutMs?: number;
  /** Wall-clock injection point for tests. */
  readonly clock?: Clock;
}

/**
 * Owns server-side operations on attachments: upload, delete, and
 * post-upload monitoring (filename/url discovery + virus-scan progress).
 * Each call to {@link monitorAttachment} starts an independent poll
 * loop; callers MUST pair each call with {@link stopMonitoring} once
 * they stop caring (typically in a `useEffect` cleanup).
 */
export class AttachmentManagementService {
  private readonly _mpl: IMplServiceClient;
  private readonly _scanPollIntervalMs: number;
  private readonly _scanTimeoutMs: number;
  private readonly _clock: Clock;

  constructor(mpl: IMplServiceClient, options: AttachmentManagementServiceOptions = {}) {
    this._mpl = mpl;
    this._scanPollIntervalMs = options.scanPollIntervalMs ?? DEFAULT_SCAN_POLL_INTERVAL_MS;
    this._scanTimeoutMs = options.scanTimeoutMs ?? DEFAULT_SCAN_TIMEOUT_MS;
    this._clock = options.clock ?? systemClock;
  }

  /**
   * Upload a file as an attachment. Resolves with the new `attachmentId`
   * once the PUT succeeds — i.e. the file is durably stored and the
   * server has registered the attachment. Note: this does NOT wait for
   * the virus scan to complete; callers that need scan progress should
   * pass the returned id to {@link monitorAttachment}.
   *
   * @throws {AttachmentTransferError} when the upload fails.
   */
  async upload(
    contentId: string,
    file: File,
    onUploadProgress: (percent: number) => void,
  ): Promise<string> {
    // Wrap a classified backend failure so the attachments UI can message the upload precisely.
    const attachment = await this._mpl
      .putContentAttachmentWithProgress(contentId, file, onUploadProgress)
      .catch(handleTransferError('upload'));
    return attachment.attachmentId;
  }

  /**
   * Fetch a single point-in-time view of an attachment. Unlike
   * {@link monitorAttachment} this does not poll — use it when you need
   * one snapshot of an already-scanned attachment (e.g. the download
   * page gating on {@link classifyScanVerdict}).
   *
   * @throws {AttachmentTransferError} when the fetch fails.
   */
  async getAttachment(contentId: string, attachmentId: string): Promise<Attachment> {
    // Wrap a classified backend failure so the attachments UI can message the load precisely.
    const { attachment } = await this._mpl
      .getAttachmentInfo(contentId, attachmentId)
      .catch(handleTransferError('load'));
    return attachment;
  }

  /**
   * Watch an attachment by polling `getAttachmentInfo`. Returns a
   * monitor whose snapshot starts as `unknown` and transitions through
   * `scanning` to `complete` (with the attachment, including OK /
   * INFECTED scan result), or `failed` on a 15-minute timeout.
   * Callers MUST pair this with {@link stopMonitoring} when they no
   * longer need updates.
   */
  monitorAttachment(contentId: string, attachmentId: string): AttachmentMonitor {
    const monitor = new AttachmentMonitorImpl(attachmentId);
    void this.pollUntilScanned(contentId, attachmentId, monitor);
    return monitor;
  }

  /**
   * Stop the poll loop for `monitor` and drop its event listeners.
   * Idempotent — safe to call after the monitor has already terminated.
   */
  stopMonitoring(monitor: AttachmentMonitor): void {
    if (monitor instanceof AttachmentMonitorImpl) monitor.stop();
  }

  private async pollUntilScanned(
    contentId: string,
    attachmentId: string,
    monitor: AttachmentMonitorImpl,
  ): Promise<void> {
    const deadline = this._clock.now() + this._scanTimeoutMs;
    while (!monitor.isStopped && !isTerminalSnapshot(monitor.snapshot)) {
      const attachment = await this.tryFetchAttachment(contentId, attachmentId);
      if (monitor.isStopped) return;
      if (attachment) applyPollResult(monitor, attachmentId, attachment);
      if (isTerminalSnapshot(monitor.snapshot)) return;

      if (this._clock.now() >= deadline) {
        const error = new Error(
          `Attachment scan did not complete within ${this._scanTimeoutMs / 60000} minutes.`,
        );
        log.error({ contentId, attachmentId }, 'AttachmentManagementService: scan timed out');
        monitor.setFailed(error);
        return;
      }
      await this._clock.delay(this._scanPollIntervalMs);
    }
  }

  /**
   * One getAttachmentInfo call. Returns undefined on transient failure so
   * the caller keeps polling — legacy AttachmentListItem swallows poll
   * errors the same way.
   */
  private async tryFetchAttachment(
    contentId: string,
    attachmentId: string,
  ): Promise<Attachment | undefined> {
    try {
      const { attachment } = await this._mpl.getAttachmentInfo(contentId, attachmentId);
      return attachment;
    } catch (err) {
      log.warn({ err, contentId, attachmentId }, 'AttachmentManagementService: scan poll failed');
      return undefined;
    }
  }
}
